import EventMapEmitter, { EventHandlerMap } from '../../utils/EventMapEmitter';
import Timer from './Timer';
import { notify } from 'app/services/errorReporter';

export interface IWatchdogEventMap {
  /** Number of milliseconds until the 'timeout' event */
  warning: number;
  timeout: null;
}

export type IWatchdogPhase = keyof IWatchdogEventMap | 'off';

/**
 * Watchdog configuration
 */
export interface IWatchdogConfig {
  /** Time after which to show the warning */
  readonly warningDelay: number;
  /**
   * After `warning` event fires, `timeout` event will fire after
   * `baseTimeout` milliseconds. Each subsequent time watchdog is
   * started, this time will double, up to `maxTimeout`.
   */
  readonly baseTimeout: number;
  /**
   * Maximum time between `warning` and `timeout` events. When `maxTimeout` is reached, an error
   * report will be sent.
   */
  readonly maxTimeout: number;
  /** Identifier for this watchdog used in error reports */
  readonly name: string;
}

/**
 * Watchdog implementation that will increase the timeout each time it's started.
 *
 * Fires two events:
 * - `warning` after a fixed period of time `config.warningDelay`, value
 *   of the event is number of ms until the `timeout` event
 * - `timeout` after the `warning` event and `config.baseTimeout` but
 *   that doubles for each consecutive restarts of the watchdog,
 *   never larger than `config.maxTimeout`. This inceasing also
 *   starts over after `.stop()` method is called.
 */
export default class Watchdog implements Pick<EventMapEmitter<IWatchdogEventMap>, 'bindEvents' | 'unbindEvents'> {
  private readonly emitter: EventMapEmitter<IWatchdogEventMap> = new EventMapEmitter();
  private readonly warning = new Timer(() => this.onWarning());
  private readonly timeout = new Timer(() => this.onTimeout());
  private counter: number = 0;
  private didNotify: boolean = false;

  public constructor(private readonly config: IWatchdogConfig) {}

  public bindEvents(eventMap: EventHandlerMap<IWatchdogEventMap>) {
    this.emitter.bindEvents(eventMap);
  }

  public unbindEvents(eventMap: EventHandlerMap<IWatchdogEventMap>) {
    this.emitter.unbindEvents(eventMap);
  }

  /**
   * Starts the first watchdog. Noop if already running
   */
  public start() {
    if (this.phase !== 'off') {
      return;
    }

    this.warning.start(this.config.warningDelay);
  }

  /**
   * Stops the watchdog and resets internal counter for increasing
   * the timeout.
   */
  public stop() {
    this.warning.stop();
    this.timeout.stop();
    this.counter = 0;
    this.didNotify = false;
  }

  /**
   * Returns current phase of the watchdog:
   * - `off` when stopped
   * - `warning` if next event will be `warning`
   * - `timeout` if the next event will be `timeout` (so `warning`
   *   has already fired)
   */
  public get phase(): IWatchdogPhase {
    if (this.warning.isRunning) {
      return 'warning';
    } else if (this.timeout.isRunning) {
      return 'timeout';
    } else {
      return 'off';
    }
  }

  private get timeoutDelay() {
    const { baseTimeout, maxTimeout } = this.config;

    return Math.min(
      maxTimeout,
      baseTimeout * Math.pow(2, this.counter),
    );
  }

  private onWarning() {
    const { config } = this;

    if (this.timeoutDelay === config.maxTimeout && !this.didNotify) {
      notify(new Error(`${config.name} watchdog hit maximum timeout delay`), {
        context: {
          config,
        },
      });

      this.didNotify = true;
    }

    this.emitter.emit('warning', this.timeoutDelay);
    this.timeout.start(this.timeoutDelay);
    ++this.counter;
  }

  private onTimeout() {
    this.emitter.emit('timeout', null);
  }
}
